import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import useHotkeys from '@lyearn/core/hooks/useHotkeys';
import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { Close, SearchFill } from '@lyearn/icons';
import { Input, LinkButton, Stack, Typography } from '@lyearn/ui';
import { KeyCodes } from '@/consts/keyCodes';
import useDebounceState from '@/hooks/useDebounceState';

import { SearchBarRef, SearchProps } from '../types';

const SHORTCUT_KEY = '/';

const INPUT_PROPS = {
  'data-testid': 'search-input',
};

const onKeyDown =
  (onEnter: () => void) => (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (e.key === KeyCodes.Enter) {
      if (!e.shiftKey) {
        onEnter();
        e.preventDefault();
      }
    }
  };

const SearchBar = forwardRef<SearchBarRef, SearchProps>((props, ref) => {
  const {
    className,
    searchText: value,
    setSearch: onChange,
    placeholder,
    onEnter,
    delay,
    size,
    autoFocus,
  } = props;
  const { t } = useTranslation();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [searchText, setSearchText, debounceSearchText] = useDebounceState(value, delay);
  const { isMobile } = useScreen();

  /**
   * exposed clear() to clear the input state from outside
   * this is done as the value given to this component is controlled by url-query-param
   * */
  useImperativeHandle(
    ref,
    () => {
      return {
        clear() {
          setSearchText('');
        },
        focus() {
          inputRef.current?.focus();
        },
      };
    },
    [setSearchText],
  );

  const onKeyPress = useCallback((event: KeyboardEvent) => {
    event.preventDefault();
    inputRef.current?.focus();
  }, []);

  useHotkeys(SHORTCUT_KEY, onKeyPress, [onKeyPress]);
  useEffect(() => {
    onChange(debounceSearchText);
  }, [debounceSearchText, onChange]);

  const onClear = useCallback(() => {
    setSearchText('');
  }, [setSearchText]);

  const onChangeText = useCallback(
    (searchValue: string) => {
      setSearchText(searchValue);
    },
    [setSearchText],
  );

  const endAdornment = searchText.length ? (
    <LinkButton
      aria-label={t('Common:Actions.ClearSearch')}
      className="py-2 px-4"
      color={'internal'}
      onClick={onClear}>
      <Close />
    </LinkButton>
  ) : !isMobile ? (
    <Stack
      alignItems="center"
      className="h-24 w-24 rounded-4 bg-bg-tertiary"
      justifyContent="center">
      <Typography color="text-text-placeholder" variant="body-short-02">
        {SHORTCUT_KEY}
      </Typography>
    </Stack>
  ) : null;

  return (
    <Input
      aria-label={t('Common:PageSearch')}
      autoFocus={autoFocus}
      className={className}
      endAdornment={endAdornment}
      inputProps={INPUT_PROPS}
      inputRef={inputRef}
      placeholder={placeholder}
      role="search"
      size={size}
      startAdornment={
        <SearchFill
          className={size === 'large' ? 'h-24 w-24 flex-shrink-0' : 'h-20 w-20 flex-shrink-0'}
          fillColorToken="icon-placeholder"
        />
      }
      value={searchText}
      onChangeText={onChangeText}
      onKeyDown={onEnter ? onKeyDown(onEnter) : undefined}
    />
  );
});

export default SearchBar;
